import React, { useEffect } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { Text, Pressable } from '@atlaskit/primitives/compiled';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import Image from '@atlaskit/image';

import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

import SideNavBannerImage from '../assets/SideNavBannerImage.svg';

const i18n = defineMessages({
	AIFirstCreateSideNavTitle: {
		id: 'onboarding-ai-first-create.ai-first-create-side-nav.title',
		defaultMessage: 'You describe, Rovo creates',
		description: 'Title for AI first create side nav',
	},
	AIFirstCreateSideNavDescription: {
		id: 'onboarding-ai-first-create.ai-first-create-side-nav.description',
		defaultMessage: 'With a simple prompt, Rovo can draft a page or whiteboard.',
		description: 'Description text for AI first create side nav',
	},
	AIFirstCreateSideNavCTA: {
		id: 'onboarding-ai-first-create.ai-first-create-side-nav.cta',
		defaultMessage: 'Show me how',
		description: 'CTA button text for AI first create side nav',
	},
	AIFirstCreateSideNavDismissButton: {
		id: 'onboarding-ai-first-create.ai-first-create-side-nav.dismiss-button',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button text for AI first create side nav',
	},
});

const styles = cssMap({
	root: {
		padding: token('space.100'),
	},
	sideNav: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('border.radius'),
		overflow: 'hidden',
		transition: 'all 0.3s ease',
		position: 'relative',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	content: {
		padding: token('space.200'),
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	closeButton: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
		zIndex: 1,
	},
	imageWrapper: {
		flex: '0 0 auto',
		alignSelf: 'stretch',
		display: 'flex',
		alignItems: 'center',
	},
	cta: {
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

export const AIFirstCreateSideNav = ({
	hasUserSeenAIFirstCreateSideNav,
}: {
	hasUserSeenAIFirstCreateSideNav: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const handleDismissClick = () => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasDismissedAIFirstCreateSideNav,
			value: 'true',
		});
	};

	useEffect(() => {
		if (!hasUserSeenAIFirstCreateSideNav) {
			setOnboardingState({
				key: AIFC_ONBOARDING_STATE_KEYS.firstSeenAIFirstCreateSideNavAt,
				value: new Date().toISOString(),
			});
		}
	}, [hasUserSeenAIFirstCreateSideNav, setOnboardingState]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				actionSubject: 'aiFirstCreateSideNav',
				source: 'onboarding-ai-first-create',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<div data-testid="ai-first-create-side-nav" css={styles.root}>
			<div css={styles.sideNav}>
				<div css={styles.content}>
					<Heading size="small">{formatMessage(i18n.AIFirstCreateSideNavTitle)}</Heading>
					<Text color="color.text.subtle">
						{formatMessage(i18n.AIFirstCreateSideNavDescription)}
					</Text>
					<Pressable xcss={styles.cta}>
						<Text color="color.link.pressed" weight="semibold">
							<FormattedMessage {...i18n.AIFirstCreateSideNavCTA} />
						</Text>
					</Pressable>
				</div>
				<div css={styles.imageWrapper}>
					<Image src={SideNavBannerImage} alt="" />
				</div>
				<div css={styles.closeButton}>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						appearance="subtle"
						label={<FormattedMessage {...i18n.AIFirstCreateSideNavDismissButton} />}
						isTooltipDisabled={false}
						onClick={handleDismissClick}
						spacing="compact"
					/>
				</div>
			</div>
		</div>
	);
};
